import React, { useEffect, useRef, useState } from 'react';
import '../styles/Contact.css';
import gsap from 'gsap';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase'; // adjust path if needed
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Footer from '../components/Footer';
import phoneIcon from '../assets/icons/phone.png';

gsap.registerPlugin(ScrollTrigger);

export default function Contact() {
  const pageRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!pageRef.current) return;

    // Reveal cards/rows as they scroll into view
    const elements = gsap.utils.toArray('.reveal');
    elements.forEach((el) => {
      gsap.fromTo(
        el,
        { y: 26, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  useEffect(() => {
    // Ensure an auth user for callable functions (anonymous is fine)
    const auth = getAuth();
    signInAnonymously(auth).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }
    try {
      const fd = new FormData(formEl);
      const payload = {
        name: fd.get('name') || '',
        email: fd.get('email') || '',
        phone: fd.get('phone') || '',
        company: fd.get('company') || '',
        message: fd.get('message') || '',
      };
      const submitContact = httpsCallable(functions, 'submitContact');
      await submitContact(payload);
      setSubmitted(true);
      formEl.reset();
    } catch (err) {
      console.error('Contact submit failed:', err);
      alert('Sorry—could not send your message. Please try again shortly.');
    }
  };

  return (
    <main ref={pageRef} className="contact-page lenis-content" aria-labelledby="contact-title">
      <title>Contact us for help - Call now or Fill out a form for more information</title>
      <meta name="description" content="Send your list or photos for a fast quote. We handle insured pickup and pay in 24–48h. Clear terms, no surprises." />
      <link rel="canonical" href="https://sdindustrialsurplus.com/contact-us" />
      <meta name="robots" content="index,follow" />


      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero__inner">
          <h1 id="contact-title" className="contact-hero__title">How can we help?</h1>
          <p className="contact-hero__subtitle">
            Reach out for quotes, bulk sell-offs, or technical questions about transformers, PVF, and electrical equipment.
          </p>
        </div>
        <div className="contact-hero__overlay" aria-hidden="true" />
      </section>

      {/* Content grid */}
      <section className="contact-wrap">
        {/* Info / CTAs */}
        <aside className="contact-card info-card reveal" aria-label="Company contact information">
          <h2 className="card-title-contact">Talk to a specialist</h2>

          <div className="info-cta">
            <a href="tel:8328852369" className="btn btn-primary" aria-label="Call S&D Industrial Surplus">
              <img src={phoneIcon} alt="" className="btn-icon" /> (832) 885-2369
            </a>
            <a href="mailto:sd.surplus.purchasing@gmail.com" className="btn btn-secondary" aria-label="Email S&D Industrial Surplus">
              Email Us
            </a>
          </div>

          <div className="info-block">
            <h3>Alabama Office Address</h3>
            <p>1501 Jordan lane<br />Huntsville, Alabama 35816</p>
            <h3>Texas Office Address</h3>
            <p>11665 Fuqua, Suite E507<br />Houston, TX 77034</p>
          </div>

          <div className="info-grid">
            <div>
              <h3>Hours</h3>
              <p>Mon–Fri · 8:00a–5:00p CT</p>
            </div>
            <div>
              <h3>Response time</h3>
              <p>Same-day on business days</p>
            </div>
          </div>

          <div className="microcopy">Prefer a quick call? We can price equipment in minutes with nameplate photos.</div>
        </aside>

        {/* Form */}
        <div className="contact-card form-card reveal" role="region" aria-label="Send us a message">
          <h2 className="card-title-contact">Send us a message</h2>
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-fields">
              <input className="contact-input" id="name" name="name" type="text" autoComplete="name" required placeholder=" " />
              <label htmlFor="name">Full name</label>
            </div>

            <div className="field-grid">
              <div className="contact-fields">
                <input className="contact-input" id="email" name="email" type="email" autoComplete="email" required placeholder=" " />
                <label htmlFor="email">Email</label>
              </div>
              <div className="contact-fields">
                <input className="contact-input" id="phone" name="phone" type="tel" autoComplete="tel" placeholder=" " pattern="[+0-9()\\-\\s]{7,}" />
                <label htmlFor="phone">Phone (optional)</label>
              </div>
            </div>

            <div className="contact-fields">
              <input className="contact-input" id="company" name="company" type="text" autoComplete="organization" placeholder=" " />
              <label htmlFor="company">Company (optional)</label>
            </div>

            <div className="contact-fields">
              <textarea className="contact-input" id="message" name="message" rows="5" required placeholder=" "></textarea>
              <label htmlFor="message">How can we help?</label>
            </div>

            <div className="actions">
              <button type="submit" className="submit-btn" aria-live="polite">
                Submit
                <svg className="chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M13.172 12l-4.95-4.95 1.414-1.414L16 12l-6.364 6.364-1.414-1.414z"/></svg>
              </button>
              <span className="privacy">By submitting, you agree to our standard response practices. We never sell your information.</span>
            </div>
          </form>

          {submitted && (
            <div className="form-toast" role="status" aria-live="polite">
              Thanks! Your message has been sent.
              <button className="toast-close" onClick={() => setSubmitted(false)} aria-label="Dismiss">×</button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
